/**
 * Dot-and-bracket field paths, the address form shared by generated schema
 * `fullPath` values, flattened form keys, and `DynamicForm`'s omit lists.
 */

/**
 * Collapses array indices so a record's `teams[3].id` matches the schema's
 * `teams[0].id`. Generated schemas describe one representative item; records
 * carry as many as the API returned.
 */
export const normalizePath = (path: string): string =>
  path.replace(/\[\d+\]/g, '[]')

/** `a.b[0].c` -> `a`, `a.b`, `a.b[0]`, `a.b[0].c`. */
export const pathPrefixes = (path: string): Array<string> => {
  const prefixes: Array<string> = []
  let current = ''

  for (const segment of path.split('.')) {
    current = current === '' ? segment : `${current}.${segment}`

    const bracket = segment.indexOf('[')
    if (bracket === -1) {
      prefixes.push(current)
      continue
    }

    // `b[0][1]` contributes `…b`, `…b[0]`, then `…b[0][1]`.
    const base = current.slice(0, current.length - (segment.length - bracket))
    prefixes.push(base)
    let built = base
    for (const index of segment.slice(bracket).match(/\[\d+\]/g) ?? []) {
      built += index
      prefixes.push(built)
    }
  }

  return prefixes
}

export const isAncestorPath = (ancestor: string, path: string): boolean =>
  path.startsWith(`${ancestor}.`) || path.startsWith(`${ancestor}[`)

/** Removes the value at a dot-and-bracket path, leaving the rest of the tree intact. */
export const deleteAtPath = (target: unknown, path: string): void => {
  const segments = pathPrefixes(path)
  const parentPath = segments[segments.length - 2]
  const parent =
    parentPath === undefined ? target : readPath(target, parentPath)

  if (parent === null || typeof parent !== 'object') return

  const leaf = path.slice(parentPath === undefined ? 0 : parentPath.length)
  const index = leaf.match(/^\[(\d+)\]$/)

  if (index !== null && Array.isArray(parent)) {
    parent.splice(Number(index[1]), 1)
    return
  }

  delete (parent as { [key: string]: unknown })[leaf.replace(/^\./, '')]
}

const readPath = (target: unknown, path: string): unknown => {
  let current = target

  for (const segment of path.split('.')) {
    const bracket = segment.indexOf('[')
    const key = bracket === -1 ? segment : segment.slice(0, bracket)

    if (current === null || typeof current !== 'object') return undefined
    current = (current as { [key: string]: unknown })[key]

    for (const index of segment
      .slice(bracket === -1 ? 0 : bracket)
      .match(/\[\d+\]/g) ?? []) {
      if (!Array.isArray(current)) return undefined
      current = current[Number(index.slice(1, -1))]
    }
  }

  return current
}
